<template>
  <div class="online-test-reports">
    <!-- Student dropdown for parent role -->
    <div class="institute-drop" v-if="students.length > 1 && isParent">
      <div class="student-dropdown-list">
        <v-select
          @change="updateSelectedStudent"
          :items="students"
          v-model="selectedStudent"
          hide-details
          label="Select Student"
        ></v-select>
      </div>
    </div>

    <!-- Search field -->
    <div class="test-name-search-25jan">
      <div class="search-test-name">
        <v-text-field
          v-model="testName"
          @keydown="onSearch()"
          solo
          label="Test Name"
          dense
          outlined
          hide-details
          append-icon="search"
          autocomplete="off"
        ></v-text-field>
      </div>
    </div>

    <!-- Data table -->
    <v-card class="mx-auto normal-card">
      <div class="data-table-section assignment-table">
        <v-data-table
          :headers="headers"
          fixed-header
          :items="items"
          hide-default-header
          hide-default-footer
          class="elevation-2 cust-table"
        >
          <template v-slot:header="{ props: { headers } }">
            <TableHeader
              :headers="headers"
              :onSortChange="onSortChange"
              :onHeaderAction="onHeaderAction"
              :orderBy="orderBy"
            />
          </template>
          <template v-slot:body="{ items }">
            <tbody>
              <tr v-for="item in items" :key="item.id">
                <td>{{ item.test_name }}</td>
                <td class="duration-display assignment-120">
                  {{ formatDate(item.date_answered) }}
                </td>
                <td class="duration-display">{{ getTestType(item) }}</td>
                <td class="text-center">{{ item.target_name }}</td>
                <td class="text-center">{{ item.subjects }}</td>
                <td class="download-icon">
                  <!-- Loading indicator -->
                  <v-img
                    class="loader-img"
                    src="../../../assets/img/loading.gif"
                    v-if="item.status === downloadStatus.IN_PROGRESS"
                  ></v-img>
                  <!-- Lock icon for users without module access -->
                  <!-- <v-btn
                    v-else-if="!hasModuleAccess"
                    class="download-btn-cp3 lock-btn"
                    color="transparent"
                    depressed
                    @click="showUpgradePopup()"
                  >
                    <v-img
                      width="23px"
                      src="../../../assets/img/svg/lock-icon.svg"
                    ></v-img>
                  </v-btn> -->
                  <!-- Download button for users with module access -->
                  <v-btn
                    v-else
                    class="download-btn-cp3"
                    color="transparent"
                    depressed
                    @click="downloadTest(item)"
                  >
                    <v-img
                      width="23px"
                      src="../../../assets/img/download.svg"
                    ></v-img>
                  </v-btn>
                </td>
              </tr>
            </tbody>
          </template>
        </v-data-table>
      </div>
    </v-card>

    <!-- Pagination -->
    <div class="footer-btn">
      <div class="pull-left"></div>
      <div class="pull-right">
        <Pagination
          :currentPage="currentPage"
          :totalPage="totalPages"
          :onPageChange="onPageChange"
        />
      </div>
    </div>

    <!-- Upgrade Popup -->
    <AlertBox 
      :width="modalwidth" 
      :onClose="onClose" 
      :component="cComponent" 
      :status="status" 
      :data="alertBoxData" 
    />
  </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import moment from "moment";
import constants from "@/utils/constants.js";

export default {
  name: "OnlineTestReports",
  
  props: {
    // Test type: "aits" for AITS, "pyt" for PYT
    testType: {
      type: String,
      required: true,
      validator: (value) => ["aits", "pyt"].includes(value),
    },
  },

  computed: {
    ...mapState("userStore", ["user"]),
    
    /**
     * Convert string testType to numeric constant
     */
    numericTestType() {
      const mapping = {
        "aits": constants.TEST_TYPE.ALL_INDIA_TEST,
        "pyt": constants.TEST_TYPE.PREVIOUS_YEAR
      };
      return mapping[this.testType];
    }
  },

  data() {
    return {
      headers: [
        {
          text: "Test Name",
          value: "test_name",
        },
        {
          text: "Date",
          value: "test_date",
          class: "text-center"
        },
        {
          text: "Test Type",
          value: "test_type",
          class: "text-center",
          allText: "All Test Types",
          hideSort: true,
        },
        {
          text: "Target Exam",
          value: "target_exam",
          class: "text-center"
        },
        {
          text: "Subjects",
          value: "subjects",
          class: "text-center",
          hideSort: true,
        },
        { 
          text: "",
          value: "",
          type: "blank",
          class: "int-120"
        },
      ],
      items: [],
      currentPage: 1,
      pageSize: 10,
      totalPages: 1,
      orderBy: {
        field: "date_answered",
        order: "DESC"
      },
      selectedExamTypes: [],
      testName: null,
      typetimeout: null,
      downloadStatus: {},
      requestIds: [],
      testStatusTimeout: null,
      toasts: {},
      students: [],
      selectedStudent: null,
      currentStudentId: null,
      isParent: false,
      hasModuleAccess: false,
      status: false,
      alertBoxData: {},
      modalwidth: "500px",
      cComponent: "components/student/Tests/PastTests/UpgradeToPracticeSessionPopUp",
    };
  },

  watch: {
    selectedExamTypes(val) {
      if (val) {
        this.reqGetList();
      }
    },
    
    testType: {
      immediate: false,
      handler(newVal) {
        if (newVal) {
          this.currentPage = 1;
          this.reqGetList();
        }
      }
    },
  },

  async mounted() {
    // Wait for user data to be loaded
    if (!(this.user && this.user.subscription_details)) {
      await this.delayTime(1000);
    }

    this.downloadStatus = constants.DOWNLOAD_STATUS;
    this.checkSessionData();
    
    // Initialize student data for parent role
    if (this.user.role_id === this.roleIds.PA) {
      this.initStudentData();
    } else {
      this.currentStudentId = this.user.id;
    }

    // Check module access for download feature
    this.checkModuleAccess();

    this.reqGetList();
  },

  methods: {
    ...mapActions({
      getTestTarget: "utilStore/getTestTarget",
      getTestReportsList: "testStore/getOnlineTestReportsList",
      downloadTestPaper: "testStore/downloadTest",
      deleteDownloadData: "testStore/deleteDownloadData",
      getDownloadStatus: "testStore/getDownloadStatus",
    }),

    /**
     * Initialize student data for parent role
     */
    initStudentData() {
      this.isParent = true;
      this.students = this.user.students.map((student) => ({
        text: student.name,
        value: {
          id: student.id,
          batchId: student.batch_id,
          type: student.type,
        },
      }));
      this.selectedStudent = this.students[0].value;
      this.currentStudentId = this.selectedStudent.id;
    },

    /**
     * Update selected student (parent role)
     */
    updateSelectedStudent() {
      this.currentStudentId = this.selectedStudent.id;
      localStorage.setItem('Related_student_for_parent', JSON.stringify(this.currentStudentId));
      this.currentPage = 1;
      this.reqGetList();
    },

    /**
     * Initialize session data
     */
    checkSessionData() {
      let rp = this.$route;
      let crd = this.getCurrentRouteDetails(rp.name);

      if (rp.params.resetPage || !crd.routeName) {
        let tabDetail = {
          [this.testType]: { page: 1, requestIds: [] },
        };
        let routeDetails = {
          routeName: rp.name,
          selectedTab: this.testType,
          tabDetails: tabDetail,
        };
        this.setRouteDetails(routeDetails);
      }
      this.loadSessionData();
    },

    /**
     * Load data from session
     */
    loadSessionData() {
      let routeName = this.$route.name;
      let crd = this.getCurrentRouteDetails(routeName);
      if (crd && crd.tabDetails && crd.tabDetails[this.testType]) {
        this.requestIds = crd.tabDetails[this.testType].requestIds || [];
      }
      this.checkDownloadUpdate();
      this.reqGetTestTargets();
    },

    /**
     * Get test targets for filtering
     */
    reqGetTestTargets() {
      this.$root.showLoader = true;
      this.getTestTarget({})
        .then(() => {
          this.$root.showLoader = false;
        })
        .catch(() => {
          this.$toasted.error("Error getting exam types");
          this.$root.showLoader = false;
        });
    },

    /**
     * Get test reports list
     */
    reqGetList() {
      this.$root.showLoader = true;
      
      let reqData = {
        test_type: this.testType,
        student_id: this.currentStudentId,
        orderBy: this.orderBy,
        pagination: { page: this.currentPage, pageSize: this.pageSize }
      };

      if (this.testName) {
        reqData.search_query = this.testName;
      }

      this.getTestReportsList(reqData)
        .then((response) => {
          this.$root.showLoader = false;
          this.items = response.list;
          this.totalPages = response.pagination.pageCount;

          if(this.items.length === 0 && this.user.institute === constants.ONLINE_INSTITUTE_ID){
            if(this.testType == 'aits'){
              this.cComponent = "components/student/Tests/PastTests/AssignmentPopup";
              this.alertBoxData = {
                message: "Please answer a Test and come back here to download the Report"
              };
            } else if(this.testType == 'pyt'){
              this.cComponent = "components/student/Tests/PastTests/AssignmentPopup";
              this.alertBoxData = {
                message: "Please answer a Previous Year Test and come back here to see the Report"
              };
            }
            this.status = true;
            this.modalwidth = "500px";  
          }
        })
        .catch(() => {
          this.$toasted.error("Error getting test reports");
          this.$root.showLoader = false;
        });
    },

    /**
     * Format date to DD/MM/YYYY
     */
    formatDate(date) {
      return date ? moment(date).format("DD/MM/YYYY") : "";
    },

    /**
     * Get test type name
     */
    getTestType(item) {

      if(this.testType === 'pyt') return "PYT";
      if(this.testType === 'aits') return "AITS";

      const testType = constants.TEST_TYPES_TR.find(
        (x) => x.id === item.test_type
      );
      return testType ? testType.title : "";
    },

    /**
     * Handle page change
     */
    onPageChange(e) {
      this.currentPage = e;
      this.reqGetList();
    },

    /**
     * Handle sort change
     */
    onSortChange() {
      this.reqGetList();
    },

    /**
     * Handle header actions (filters)
     */
    onHeaderAction(e) {
      if (
        e.header &&
        e.header.type === "multidropdown" &&
        e.header.value === "test_type"
      ) {
        this.selectedExamTypes = e.data.selectedItems;
      }
    },

    /**
     * Handle search with debouncing
     */
    onSearch() {
      this.resetFields();
      if (this.typetimeout) {
        clearTimeout(this.typetimeout);
      }
      this.typetimeout = setTimeout(() => {
        this.reqGetList();
      }, 1500);
    },

    /**
     * Reset pagination fields
     */
    resetFields() {
      this.currentPage = 1;
      this.pageSize = 10;
      this.totalPages = 1;
    },

    /**
     * Download test report
     */
    downloadTest(item) {
      const existingId = this.isParent 
        ? this.currentStudentId 
        : JSON.parse(localStorage.getItem('Related_student_for_parent') || 'null');
      
      if (existingId) {
        item.related_student_for_parent_id = existingId;
      }

      let token = sessionStorage.getItem('auth_token');
      let url = existingId 
        ? `/test/download-test-report/${item.id}?token=${token}&related_student_for_parent_id=${existingId}`
        : `/test/download-test-report/${item.id}?token=${token}`;

      this.downloadTestResource(url, item);
    },

    /**
     * Download test resource and track progress
     */
    downloadTestResource(url, test) {
      // Update item status to in progress
      this.items = this.items.map((item) => {
        if (item.id === test.id) {
          item.status = constants.DOWNLOAD_STATUS.IN_PROGRESS;
        }
        return item;
      });

      // Show toast notification
      this.toasts[test.id] = this.$toasted.success(
        "PDF creation under progress. Please do not leave this page",
        { icon: 'info', duration: null, id: test.id.toString() }
      );

      this.downloadTestPaper(url)
        .then((res) => {
          this.requestIds.push(res.request_id);
          this.updateRouteDetails({ type: "requestIds", data: { requestIds: this.requestIds } });

          // Update item with request_id
          this.items = this.items.map((item) => {
            if (item.id === test.id) {
              item.request_id = res.request_id;
            }
            return item;
          });

          this.checkDownloadUpdate();
        })
        .catch((err) => {
          console.error("Download error:", err);
          this.$toasted.error("Error downloading test report");
          
          // Reset item status
          this.items = this.items.map((item) => {
            if (item.id === test.id) {
              item.status = null;
            }
            return item;
          });

          // Clear toast
          if (this.toasts[test.id]) {
            this.toasts[test.id].goAway(0);
            delete this.toasts[test.id];
          }
        });
    },

    /**
     * Check download status periodically
     */
    checkDownloadUpdate() {
      if (this.testStatusTimeout) {
        clearInterval(this.testStatusTimeout);
        this.testStatusTimeout = null;
      }

      if (this.requestIds && this.requestIds.length) {
        this.testStatusTimeout = setInterval(() => {
          this.getTestDownloadStatus();
        }, 10 * 1000);
      }
    },

    /**
     * Get download status from server
     */
    getTestDownloadStatus() {
      const reqData = {
        request_ids: this.requestIds,
        download_types: [constants.DOWNLOAD_TYPES.TEST_REPORT]
      };

      this.getDownloadStatus(reqData)
        .then(async (res) => {
          const arr = [];
          this.items.map((item) => {
            const data = res.find((r) => r.request_id == item.request_id);
            if (data && data.status === constants.DOWNLOAD_STATUS.COMPLETED) {
              item.url = data.url;
              item.status = data.status;
              
              if (item.url) {
                arr.push({
                  url: data.url,
                  request_id: data.request_id
                });
              }

              // Remove from request IDs
              this.requestIds = this.requestIds.filter(
                (rid) => rid !== data.request_id
              );

              // Clear toast
              if (this.toasts[item.id]) {
                this.toasts[item.id].goAway(0);
                delete this.toasts[item.id];
              }

              this.$toasted.success(
                `Your ${data.download_type} of ${item.test_name} PDF is successfully downloaded`,
                { icon: 'info' }
              );
            }
          });

          // Download all completed files
          for (let i = 0; i < arr.length; i++) {
            const item = arr[i];
            await this.downloadTestURL(item);
            await new Promise(resolve => setTimeout(resolve, 1000));
          }

          // Clear interval if no more pending downloads
          if (!this.requestIds || !this.requestIds.length) {
            clearInterval(this.testStatusTimeout);
            this.testStatusTimeout = null;
          }
        })
        .catch((err) => {
          console.error("Error getting download status:", err);
        });
    },

    /**
     * Download test URL
     */
    downloadTestURL(item) {
      if (item.url && item.request_id) {
        window.open(item.url, "_self");
        
        const reqData = {
          request_id: item.request_id
        };

        item.status = null;
        item.request_id = null;
        item.url = null;

        setTimeout(() => {
          this.deleteDownloadData(reqData)
            .then(() => {
              console.log("Download data deleted");
            })
            .catch((err) => {
              console.error("Error deleting download data:", err);
            });
        }, 5000);
      }
    },

    /**
     * Update route details in session
     */
    updateRouteDetails(details) {
      let routeName = this.$route.name;
      let crd = this.getCurrentRouteDetails(routeName);
      
      switch (details.type) {
        case "requestIds":
          if (crd.tabDetails[this.testType]) {
            crd.tabDetails[this.testType].requestIds = details.data.requestIds;
          }
          break;
        default:
          break;
      }
      
      this.setRouteDetails(crd);
    },

    checkModuleAccess() {
      let requiredModule = this.checkStudentUpgradedHelper(this.user.subscription_details);
      this.hasModuleAccess = !requiredModule;
    },

    showUpgradePopup() {
      this.status = this.checkStudentUpgradedHelper(
        this.user.subscription_details
      );
      this.cComponent = "components/student/Tests/PastTests/UpgradeToPracticeSessionPopUp";
      this.modalwidth = "500px";
      this.alertBoxData = {};
    },

    onClose(data) {
      this.status = data.status;
    },
  },

  beforeDestroy() {
    // Clear interval on component destroy
    if (this.testStatusTimeout) {
      clearInterval(this.testStatusTimeout);
      this.testStatusTimeout = null;
    }
  }
};
</script>

<style scoped>
.online-test-reports {
  padding: 20px;
}

.test-name-search-25jan {
  margin-bottom: 20px;
}

.search-test-name {
  max-width: 400px;
}

.download-icon {
  text-align: center;
}

.download-btn-cp3 {
  margin: 0 auto;
}

.loader-img {
  max-width: 30px;
  margin: 0 auto;
}

.institute-drop {
  margin-bottom: 20px;
}

.student-dropdown-list {
  max-width: 300px;
}

.lock-btn {
  cursor: pointer;
}

.lock-btn:hover {
  opacity: 0.7;
}
</style>

